/* =========================================================================
   BERLINDE storefront behaviour
   Cart drawer, mobile nav, sticky header, variant picker, filters.

   Rendering rule: anything that shows money or product data is re-rendered
   by Liquid through the Section Rendering API, never assembled as a string
   in JS. That keeps currency formatting correct in every market and leaves
   no path for unescaped product text to reach innerHTML.
   ========================================================================= */
(function () {
  'use strict';

  var CART_SECTION = 'berlinde-cart';

  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }

  function on(type, selector, handler) {
    document.addEventListener(type, function (event) {
      var el = event.target instanceof Element ? event.target.closest(selector) : null;
      if (el) handler(event, el);
    });
  }

  /* --- Scrolling ---------------------------------------------------------
     Horizon scrolls an inner .page-wrapper; html/body are overflow:hidden and
     the window never fires scroll. Which element is the real scroller depends
     on content height, and that is not settled at DOMContentLoaded, so resolve
     it on every read and listen to both candidates rather than picking once.
     ---------------------------------------------------------------------- */

  function scroller() {
    var wrapper = $('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
    return null;
  }

  function scrollTop() {
    var el = scroller();
    if (el) return el.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  function onScroll(handler) {
    var wrapper = $('.page-wrapper');
    if (wrapper) wrapper.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('scroll', handler, { passive: true });
  }

  /* --- Scroll locking ----------------------------------------------------- */

  var locks = 0;
  var lockedEl = null;
  var lockedPrev = '';

  function lockScroll(lock) {
    if (lock) {
      locks += 1;
      if (locks > 1) return;
      lockedEl = scroller() || document.documentElement;
      lockedPrev = lockedEl.style.overflow;
      lockedEl.style.overflow = 'hidden';
    } else {
      locks = Math.max(0, locks - 1);
      if (locks > 0 || !lockedEl) return;
      lockedEl.style.overflow = lockedPrev;
      lockedEl = null;
    }
  }

  // The theme editor re-renders sections underneath us; a half-open drawer must
  // not leave the page permanently unscrollable.
  function releaseAllLocks() {
    if (lockedEl) lockedEl.style.overflow = lockedPrev;
    lockedEl = null;
    locks = 0;
  }

  /* --- Cart drawer -------------------------------------------------------- */

  function isCartOpen() {
    var cart = $('[data-bl-cart]');
    return !!cart && cart.dataset.open === 'true';
  }

  function isNavOpen() {
    var nav = $('[data-bl-nav]');
    return !!nav && nav.dataset.open === 'true';
  }

  function showScrim(show) {
    var scrim = $('[data-bl-scrim]');
    if (scrim) scrim.dataset.open = show ? 'true' : 'false';
  }

  function openCart() {
    var cart = $('[data-bl-cart]');
    if (!cart || cart.dataset.open === 'true') return;
    shuffleUpsellTrack(cart);
    cart.dataset.open = 'true';
    cart.setAttribute('aria-hidden', 'false');
    showScrim(true);
    lockScroll(true);
    var close = cart.querySelector('[data-bl-cart-close]');
    if (close) close.focus();
  }

  function closeCart() {
    var cart = $('[data-bl-cart]');
    if (!cart || cart.dataset.open !== 'true') return;
    cart.dataset.open = 'false';
    cart.setAttribute('aria-hidden', 'true');
    if (!isNavOpen()) showScrim(false);
    lockScroll(false);
    // Hand focus back to what opened it, or it lands on <body>.
    var trigger = $('[data-bl-cart-open]');
    if (trigger) trigger.focus();
  }

  /* --- Cart requests ------------------------------------------------------
     Line numbers shift the moment a line is removed, so a second request fired
     against the markup of the first would edit the wrong product. One request
     at a time, and the drawer is re-read from the server between them.
     ---------------------------------------------------------------------- */

  var cartBusy = false;

  function setBusy(busy) {
    cartBusy = busy;
    var cart = $('[data-bl-cart]');
    if (cart) {
      cart.setAttribute('data-busy', busy ? 'true' : 'false');
      cart.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
  }

  function cartSectionId() {
    var host = $('[data-bl-cart-host]');
    return (host && host.getAttribute('data-bl-cart-section-id')) || CART_SECTION;
  }

  function sectionsUrl(sectionId) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + '?sections=' + encodeURIComponent(sectionId);
  }

  function preserveAndSyncUpsell(currentHost, freshHost) {
    var currentUpsell = $('.bl-cart-upsell', currentHost);
    var freshUpsell = $('.bl-cart-upsell', freshHost);

    // A section response can omit product_list settings. Keep the last complete
    // server-rendered upsell instead of losing it during the cart redraw.
    if (!freshUpsell && currentUpsell) {
      var freshBody = $('.bl-cart__body', freshHost);
      if (freshBody) {
        freshUpsell = currentUpsell.cloneNode(true);
        freshBody.appendChild(freshUpsell);
      }
    }

    if (!freshUpsell) return;

    var cartProductIds = {};
    $$('[data-bl-cart-product-id]', freshHost).forEach(function (row) {
      cartProductIds[row.getAttribute('data-bl-cart-product-id')] = true;
    });

    var visibleCards = 0;
    $$('[data-bl-upsell-product-id]', freshUpsell).forEach(function (card) {
      var inCart = !!cartProductIds[card.getAttribute('data-bl-upsell-product-id')];
      card.hidden = inCart;
      card.setAttribute('data-bl-upsell-in-cart', inCart ? 'true' : 'false');
      if (!inCart) visibleCards += 1;
    });
    freshUpsell.setAttribute('data-bl-upsell-visible-count', String(visibleCards));
    freshUpsell.hidden = visibleCards === 0;
  }

  function visibleUpsellCards(upsell) {
    return $$('[data-bl-upsell-product-id]', upsell).filter(function (card) {
      return !card.hidden && card.getAttribute('data-bl-upsell-in-cart') !== 'true';
    });
  }

  function updateUpsellControls(upsell, index) {
    var cards = visibleUpsellCards(upsell);
    var lastIndex = Math.max(0, cards.length - 1);
    index = Math.max(0, Math.min(index, lastIndex));
    upsell.setAttribute('data-bl-upsell-index', String(index));

    var previous = $('[data-bl-upsell-prev]', upsell);
    var next = $('[data-bl-upsell-next]', upsell);
    if (previous) previous.disabled = cards.length <= 1 || index === 0;
    if (next) next.disabled = cards.length <= 1 || index === lastIndex;

    $$('[data-bl-upsell-dot]', upsell).forEach(function (dot, dotIndex) {
      var active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  function goToUpsellSlide(upsell, index, animate) {
    var slider = $('[data-bl-upsell-slider]', upsell);
    var cards = visibleUpsellCards(upsell);
    if (!slider || !cards.length) return;
    index = Math.max(0, Math.min(index, cards.length - 1));
    var sliderBox = slider.getBoundingClientRect();
    var cardBox = cards[index].getBoundingClientRect();
    slider.scrollTo({
      left: slider.scrollLeft + cardBox.left - sliderBox.left,
      behavior: animate ? 'smooth' : 'auto'
    });
    updateUpsellControls(upsell, index);
  }

  function shuffleUpsellTrack(scope) {
    var root = scope || document;
    var tracks = root.querySelectorAll ? Array.prototype.slice.call(root.querySelectorAll('[data-bl-upsell-track]')) : [];
    if (root instanceof Element && root.matches && root.matches('[data-bl-upsell-track]')) {
      tracks = [root];
    }
    tracks.forEach(function (track) {
      var cards = Array.prototype.slice.call(track.querySelectorAll('[data-bl-upsell-card]'));
      if (cards.length <= 1) return;
      for (var i = cards.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var temp = cards[i];
        cards[i] = cards[j];
        cards[j] = temp;
      }
      cards.forEach(function (card) {
        track.appendChild(card);
      });
      track.scrollLeft = 0;
    });
  }

  function initUpsellSliders(scope) {
    shuffleUpsellTrack(scope);
    $$('.bl-cart-upsell', scope || document).forEach(function (upsell) {
      var slider = $('[data-bl-upsell-slider]', upsell);
      var pagination = $('[data-bl-upsell-pagination]', upsell);
      var cards = visibleUpsellCards(upsell);
      if (!slider || !pagination) return;

      pagination.innerHTML = '';
      cards.forEach(function (_card, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bl-cart-upsell__pagination-dot';
        dot.setAttribute('data-bl-upsell-dot', String(index));
        dot.setAttribute('aria-label', 'Show upsell product ' + (index + 1));
        pagination.appendChild(dot);
      });

      if (!slider._blUpsellBound) {
        slider._blUpsellBound = true;
        var frame = null;
        slider.addEventListener('scroll', function () {
          if (frame) window.cancelAnimationFrame(frame);
          frame = window.requestAnimationFrame(function () {
            var width = slider.clientWidth || 1;
            updateUpsellControls(upsell, Math.round(slider.scrollLeft / width));
          });
        }, { passive: true });
      }

      var initialIndex = Number(upsell.getAttribute('data-bl-upsell-index')) || 0;
      updateUpsellControls(upsell, initialIndex);
      window.requestAnimationFrame(function () {
        goToUpsellSlide(upsell, initialIndex, false);
      });
    });
  }

  on('click', '[data-bl-upsell-prev]', function (e, button) {
    e.preventDefault();
    var upsell = button.closest('.bl-cart-upsell');
    if (!upsell) return;
    goToUpsellSlide(upsell, (Number(upsell.getAttribute('data-bl-upsell-index')) || 0) - 1, true);
  });

  on('click', '[data-bl-upsell-next]', function (e, button) {
    e.preventDefault();
    var upsell = button.closest('.bl-cart-upsell');
    if (!upsell) return;
    goToUpsellSlide(upsell, (Number(upsell.getAttribute('data-bl-upsell-index')) || 0) + 1, true);
  });

  on('click', '[data-bl-upsell-dot]', function (e, dot) {
    e.preventDefault();
    var upsell = dot.closest('.bl-cart-upsell');
    if (!upsell) return;
    goToUpsellSlide(upsell, Number(dot.getAttribute('data-bl-upsell-dot')) || 0, true);
  });

  window.BerlindeUpsell = { init: initUpsellSliders };
  window.BerlindeCart = { open: openCart, close: closeCart, refresh: refreshCart };

  function refreshCart() {
    var hadFocusInside = false;
    var host = $('[data-bl-cart-host]');
    var sectionId = cartSectionId();
    if (host && document.activeElement && host.contains(document.activeElement)) hadFocusInside = true;

    return fetch(sectionsUrl(sectionId))
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data) return;
        var sectionHtml = data[sectionId] || data[CART_SECTION];
        if (!sectionHtml) return;
        var target = $('[data-bl-cart-host]');
        if (!target) return;
        var wasOpen = isCartOpen();
        var parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var fresh = parsed.querySelector('[data-bl-cart-host]');
        if (!fresh) return;

        preserveAndSyncUpsell(target, fresh);
        target.innerHTML = fresh.innerHTML;
        initUpsellSliders(target);

        if (wasOpen) {
          var cart = $('[data-bl-cart]');
          if (cart) {
            cart.dataset.open = 'true';
            cart.setAttribute('aria-hidden', 'false');
          }
          showScrim(true);
          // The node the user was on has just been destroyed.
          if (hadFocusInside) {
            var close = $('[data-bl-cart-close]');
            if (close) close.focus();
          }
        }
        syncCartCount();
      })
      .catch(function () { /* keep the last good drawer rather than blanking it */ });
  }

  function syncCartCount() {
    var source = $('[data-bl-cart-count-source]');
    if (!source) return;
    // Header badge shows the bare number; the drawer label is "5 Artikel".
    var value = source.getAttribute('data-bl-cart-count-value') || source.textContent.trim();
    $$('[data-bl-cart-count]').forEach(function (el) {
      el.textContent = value;
      el.setAttribute('data-count', value);
      if (parseInt(value, 10) === 0 || value === '0' || value === '') {
        el.classList.add('is-empty');
      } else {
        el.classList.remove('is-empty');
      }
    });
  }

  function cartRequest(url, body) {
    setBusy(true);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw new Error((json && json.description) || 'cart');
          return json;
        });
      })
      .then(function () { return refreshCart(); })
      .finally(function () { setBusy(false); });
  }

  on('click', '[data-bl-cart-open]', function (e) { e.preventDefault(); openCart(); });
  on('click', '[data-bl-cart-close]', function (e) { e.preventDefault(); closeCart(); });
  on('click', '[data-bl-scrim]', function (e) { e.preventDefault(); closeCart(); closeNav(); });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var openOverlay = $('[data-bl-variant-overlay][style*="display: flex"], [data-bl-variant-overlay][style*="display:flex"]');
    if (openOverlay) {
      closeAllVariantOverlays();
      return;
    }
    closeCart();
    closeNav();
  });

  on('click', '[data-bl-qty]', function (e, el) {
    e.preventDefault();
    if (cartBusy) return;
    var line = parseInt(el.getAttribute('data-bl-line'), 10);
    var next = parseInt(el.getAttribute('data-bl-qty'), 10);
    if (!line || isNaN(next)) return;
    var stepper = el.closest('[data-bl-stepper]');
    if (stepper) stepper.classList.add('is-loading');
    var item = el.closest('[data-bl-cart-item]');
    if (item) item.classList.add('is-loading');
    cartRequest('/cart/change.js', { line: line, quantity: Math.max(0, next) })
      .catch(function () {
        // Stock ran out or the line moved; the server view is the truth.
        refreshCart();
      });
  });

  on('click', '.bl-cart__item-remove', function (e, el) {
    var item = el.closest('[data-bl-cart-item]');
    if (item) item.classList.add('is-loading');
  });

  /* --- Cart variant popup overlay ----------------------------------------- */

  function closeAllVariantOverlays() {
    $$('[data-bl-variant-overlay]').forEach(function (ov) {
      ov.style.display = 'none';
    });
    $$('[data-bl-open-overlay]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  on('click', '[data-bl-open-overlay]', function (e, btn) {
    e.preventDefault();
    var item = btn.closest('[data-bl-cart-item]');
    if (!item) return;
    var overlay = item.querySelector('[data-bl-variant-overlay]');
    if (!overlay) return;

    var isOpen = overlay.style.display === 'flex' || getComputedStyle(overlay).display === 'flex';
    closeAllVariantOverlays();

    if (!isOpen) {
      overlay.style.display = 'flex';
      btn.setAttribute('aria-expanded', 'true');
    }
  });

  on('click', '[data-bl-close-overlay]', function (e) {
    e.preventDefault();
    closeAllVariantOverlays();
  });

  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-bl-variant-overlay]') && !e.target.closest('[data-bl-open-overlay]')) {
      closeAllVariantOverlays();
    }
  });

  on('click', '[data-bl-opt-pill]', function (e, pill) {
    e.preventDefault();
    if (pill.classList.contains('is-disabled')) return;
    var overlay = pill.closest('[data-bl-variant-overlay]');
    if (!overlay) return;
    var group = pill.closest('[data-opt-pos]');
    if (!group) return;

    // 1. Mark pill as selected
    group.querySelectorAll('[data-bl-opt-pill]').forEach(function (p) {
      p.classList.remove('is-selected');
    });
    pill.classList.add('is-selected');

    // 2. Update value label
    var valLabel = group.querySelector('[data-bl-opt-val-label]');
    if (valLabel) valLabel.textContent = pill.getAttribute('data-val') || '';

    // 3. Read all selected option values
    var selectedVals = [];
    overlay.querySelectorAll('[data-opt-pos]').forEach(function (grp) {
      var sel = grp.querySelector('[data-bl-opt-pill].is-selected');
      selectedVals.push(sel ? sel.getAttribute('data-val') : null);
    });

    // 4. Parse variants JSON
    var jsonEl = overlay.querySelector('[data-bl-variants-json]');
    if (!jsonEl) return;
    var variants = [];
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (err) {
      return;
    }

    // 5. Cross-option availability check on all pills
    var groups = overlay.querySelectorAll('[data-opt-pos]');
    if (groups.length > 1) {
      groups.forEach(function (grp) {
        var pos = parseInt(grp.getAttribute('data-opt-pos'), 10);
        grp.querySelectorAll('[data-bl-opt-pill]').forEach(function (p) {
          var pVal = p.getAttribute('data-val');
          var isAvail = variants.some(function (v) {
            var m1 = pos === 1 ? v.option1 === pVal : (selectedVals[0] ? v.option1 === selectedVals[0] : true);
            var m2 = pos === 2 ? v.option2 === pVal : (selectedVals[1] ? v.option2 === selectedVals[1] : true);
            var m3 = pos === 3 ? v.option3 === pVal : (selectedVals[2] ? v.option3 === selectedVals[2] : true);
            return m1 && m2 && m3 && v.available;
          });
          if (!isAvail) {
            p.classList.add('is-unavailable');
          } else {
            p.classList.remove('is-unavailable');
          }
        });
      });
    }

    // 6. Find matched variant
    var matched = variants.find(function (v) {
      var m1 = selectedVals[0] ? v.option1 === selectedVals[0] : true;
      var m2 = selectedVals[1] ? v.option2 === selectedVals[1] : true;
      var m3 = selectedVals[2] ? v.option3 === selectedVals[2] : true;
      return m1 && m2 && m3;
    });

    // 7. Update apply button & status message
    var applyBtn = overlay.querySelector('[data-bl-apply-variant]');
    var statusEl = overlay.querySelector('[data-bl-variant-status]');
    var applyLabel = applyBtn ? applyBtn.querySelector('.bl-cart__variant-apply-label') : null;
    var saveText = (applyBtn && applyBtn.getAttribute('data-label-save')) || 'Übernehmen';
    var soldOutText = (applyBtn && applyBtn.getAttribute('data-label-soldout')) || 'Ausverkauft';

    if (matched) {
      applyBtn.setAttribute('data-new-id', String(matched.id));
      if (matched.available) {
        applyBtn.disabled = false;
        if (applyLabel) applyLabel.textContent = saveText;
        if (statusEl) {
          statusEl.textContent = '';
          statusEl.className = 'bl-cart__variant-status-msg';
        }
      } else {
        applyBtn.disabled = true;
        if (applyLabel) applyLabel.textContent = soldOutText;
        if (statusEl) {
          statusEl.textContent = soldOutText;
          statusEl.className = 'bl-cart__variant-status-msg is-soldout';
        }
      }
    } else {
      applyBtn.disabled = true;
      if (applyLabel) applyLabel.textContent = soldOutText;
      if (statusEl) {
        statusEl.textContent = soldOutText;
        statusEl.className = 'bl-cart__variant-status-msg is-soldout';
      }
    }
  });

  on('click', '[data-bl-apply-variant]', function (e, btn) {
    e.preventDefault();
    if (cartBusy || btn.disabled) return;
    var line = parseInt(btn.getAttribute('data-line'), 10);
    var currentId = parseInt(btn.getAttribute('data-current-id'), 10);
    var newId = parseInt(btn.getAttribute('data-new-id'), 10);
    var qty = parseInt(btn.getAttribute('data-qty'), 10) || 1;

    if (!line || !newId) return;

    if (currentId && currentId === newId) {
      closeAllVariantOverlays();
      return;
    }

    btn.classList.add('is-loading');
    btn.disabled = true;
    var item = btn.closest('[data-bl-cart-item]');
    if (item) item.classList.add('is-loading');

    setBusy(true);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: 0 })
    })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: newId, quantity: qty }] })
        });
      })
      .then(function () {
        return refreshCart();
      })
      .catch(function () {
        refreshCart();
      })
      .finally(function () {
        setBusy(false);
      });
  });

  on('change', '.bl-upsell-variant-select', function (e, sel) {
    var card = sel.closest('[data-bl-upsell-card]');
    if (!card) return;
    var addBtn = card.querySelector('[data-bl-upsell-add]');
    if (!addBtn) return;
    sel.style.borderColor = '#D9D1C3';
    sel.style.color = '#1D1F22';
    if (sel.value) {
      addBtn.setAttribute('data-variant-id', sel.value);
      addBtn.style.background = '#1D1F22';
      addBtn.style.color = '#FBF9F5';
    } else {
      addBtn.removeAttribute('data-variant-id');
      addBtn.style.background = '#E9E3D8';
      addBtn.style.color = '#1D1F22';
    }
  });

  on('click', '[data-bl-upsell-add]', function (e, btn) {
    e.preventDefault();
    if (cartBusy) return;
    var card = btn.closest('[data-bl-upsell-card]');
    var variantId = btn.getAttribute('data-variant-id');
    var select = card ? card.querySelector('.bl-upsell-variant-select') : null;
    if (!variantId && select) {
      select.style.borderColor = '#9C4A26';
      select.style.color = '#9C4A26';
      var defaultOpt = select.querySelector('option[disabled]');
      if (defaultOpt) defaultOpt.textContent = defaultOpt.getAttribute('data-required-text') || 'Größe wählen';
      select.focus();
      return;
    }
    if (!variantId) return;
    btn.classList.add('is-loading');
    if (card) card.classList.add('is-adding');
    cartRequest('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] })
      .then(function () {
        refreshCart();
      });
  });

  var drag = null;
  on('pointerdown', '[data-bl-upsell-track]', function (e, el) {
    if (e.target.closest('select, button, a')) return;
    if (e.pointerType && e.pointerType !== 'mouse') return;
    drag = { el: el, x: e.clientX, sl: el.scrollLeft, moved: false, id: e.pointerId };
  });
  window.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) > 4) {
      drag.moved = true;
      drag.el.classList.add('is-dragging');
      drag.el.style.scrollSnapType = 'none';
      drag.el.style.scrollBehavior = 'auto';
      try { drag.el.setPointerCapture(drag.id); } catch (err) {}
    }
    if (drag.moved) {
      drag.el.scrollLeft = drag.sl - dx;
    }
  });
  window.addEventListener('pointerup', function (e) {
    if (!drag) return;
    var el = drag.el;
    var moved = drag.moved;
    var dx = e.clientX - drag.x;
    drag = null;
    if (!moved) return;
    el.classList.remove('is-dragging');
    el.style.scrollSnapType = 'x mandatory';
    el.style.scrollBehavior = 'smooth';
    if (Math.abs(dx) > 35) {
      var step = dx > 0 ? -276 : 276;
      el.scrollBy({ left: step, behavior: 'smooth' });
    }
    var stop = function (ev) { ev.stopPropagation(); ev.preventDefault(); };
    el.addEventListener('click', stop, true);
    setTimeout(function () { el.removeEventListener('click', stop, true); }, 0);
  });

  /* --- Add to cart -------------------------------------------------------- */

  on('submit', 'form[data-bl-add]', function (e, form) {
    e.preventDefault();
    if (cartBusy) return;

    var button = form.querySelector('[data-bl-atc]');
    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value) return;
    if (button && button.getAttribute('aria-disabled') === 'true') return;

    var qtyField = form.querySelector('[name="quantity"]');
    var error = form.querySelector('[data-bl-add-error]');
    var original = button ? button.textContent : '';

    if (error) error.hidden = true;
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.dataset.blPending = 'true';
      var busyLabel = button.getAttribute('data-bl-adding');
      if (busyLabel) button.textContent = busyLabel;
    }

    cartRequest('/cart/add.js', {
      items: [{ id: Number(idField.value), quantity: qtyField ? Number(qtyField.value) || 1 : 1 }]
    })
      .then(function () { openCart(); })
      .catch(function () { if (error) error.hidden = false; })
      .finally(function () {
        if (!button) return;
        delete button.dataset.blPending;
        // The shopper may have switched to a sold-out variant while this was in
        // flight; in that case the picker owns the button, not this handler.
        if (button.dataset.blUnavailable === 'true') return;
        button.removeAttribute('aria-disabled');
        button.textContent = original;
      });
  });

  /* --- Mobile navigation --------------------------------------------------- */

  function closeNav() {
    var nav = $('[data-bl-nav]');
    if (!nav || nav.dataset.open !== 'true') return;
    nav.dataset.open = 'false';
    nav.setAttribute('aria-hidden', 'true');
    if (!isCartOpen()) showScrim(false);
    lockScroll(false);
    var trigger = $('[data-bl-nav-open]');
    if (trigger) trigger.focus();
  }

  on('click', '[data-bl-nav-open]', function (e) {
    e.preventDefault();
    var nav = $('[data-bl-nav]');
    if (!nav || nav.dataset.open === 'true') return;
    nav.dataset.open = 'true';
    nav.setAttribute('aria-hidden', 'false');
    showScrim(true);
    lockScroll(true);
    var close = nav.querySelector('[data-bl-nav-close]');
    if (close) close.focus();
  });
  on('click', '[data-bl-nav-close]', function (e) { e.preventDefault(); closeNav(); });

  /* --- Announcement dismissal ---------------------------------------------- */

  var ANN_KEY = 'berlinde:announcement';

  function initAnnouncement() {
    var bar = $('[data-bl-announcement]');
    if (!bar) return;
    var stamp = bar.getAttribute('data-bl-announcement');
    try {
      if (stamp && window.localStorage.getItem(ANN_KEY) === stamp) bar.hidden = true;
    } catch (err) { /* private browsing: just show it */ }
  }

  on('click', '[data-bl-announcement-close]', function (e) {
    e.preventDefault();
    var bar = $('[data-bl-announcement]');
    if (!bar) return;
    bar.hidden = true;
    try {
      window.localStorage.setItem(ANN_KEY, bar.getAttribute('data-bl-announcement') || '1');
    } catch (err) { /* nothing to do */ }
  });

  /* --- Sticky header shrink ------------------------------------------------ */

  var headerBound = false;

  function applyHeaderShrink() {
    var header = $('[data-bl-header]');
    if (!header || !header.classList.contains('bl-header--sticky')) return;
    header.classList.toggle('bl-header--shrunk', scrollTop() > 200);
  }

  function initHeaderShrink() {
    if (!headerBound) {
      headerBound = true;
      var ticking = false;
      onScroll(function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          applyHeaderShrink();
          ticking = false;
        });
      });
    }
    applyHeaderShrink();
  }

  /* --- Collection filters -------------------------------------------------- */

  // Checkboxes and the sort select submit Shopify's own filter form. Number
  // inputs are left alone so a shopper can finish typing a price.
  on('change', '[data-bl-filter-form] input[type="checkbox"], [data-bl-filter-form] select', function (e, el) {
    var form = el.closest('[data-bl-filter-form]');
    if (form) form.submit();
  });

  // Only one filter dropdown open at a time.
  document.addEventListener('click', function (e) {
    $$('[data-bl-filter][open]').forEach(function (details) {
      if (!details.contains(e.target)) details.removeAttribute('open');
    });
  });

  /* --- Product variants ----------------------------------------------------- */

  function initVariants() {
    $$('[data-bl-product]').forEach(function (scope) {
      if (scope.dataset.blBound === 'true') return;
      var dataEl = scope.querySelector('[data-bl-variants]');
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
      scope.dataset.blBound = 'true';

      function selected() {
        return $$('[data-bl-option]', scope)
          .filter(function (input) { return input.checked; })
          .sort(function (a, b) { return Number(a.dataset.blOptionIndex) - Number(b.dataset.blOptionIndex); })
          .map(function (input) { return input.value; });
      }

      function update() {
        var chosen = selected();

        $$('[data-bl-option-display]', scope).forEach(function (out) {
          var index = Number(out.getAttribute('data-bl-option-display'));
          out.textContent = chosen[index] || '';
        });

        var match = variants.find(function (v) {
          return v.options.length === chosen.length && v.options.every(function (o, i) { return o === chosen[i]; });
        });

        var idField = scope.querySelector('[data-bl-variant-id]');
        var button = scope.querySelector('[data-bl-atc]');

        function markUnavailable(labelAttr) {
          if (!button) return;
          button.dataset.blUnavailable = 'true';
          button.setAttribute('aria-disabled', 'true');
          var label = button.getAttribute(labelAttr);
          if (label) button.textContent = label;
        }

        if (!match) {
          if (idField) idField.value = '';
          markUnavailable('data-bl-unavailable');
          return;
        }

        if (idField) idField.value = match.id;

        if (button) {
          if (match.available) {
            delete button.dataset.blUnavailable;
            // Do not steal the button back while an add is still in flight.
            if (button.dataset.blPending !== 'true') {
              button.removeAttribute('aria-disabled');
              var ok = button.getAttribute('data-bl-available');
              if (ok) button.textContent = ok;
            }
          } else {
            markUnavailable('data-bl-soldout');
          }
        }

        $$('[data-bl-price-for]', scope).forEach(function (node) {
          node.hidden = node.getAttribute('data-bl-price-for') !== String(match.id);
        });

        if (match.media_id) {
          var gallery = scope.querySelector('[data-bl-gallery]');
          var target = scope.querySelector('[data-bl-media="' + match.media_id + '"]');
          if (gallery && target && target.parentElement === gallery) gallery.prepend(target);
        }

        if (window.history && window.history.replaceState) {
          try {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', match.id);
            window.history.replaceState({}, '', url.toString());
          } catch (err) { /* nothing to do */ }
        }
      }

      scope.addEventListener('change', function (e) {
        if (e.target instanceof Element && e.target.hasAttribute('data-bl-option')) update();
      });
      update();
    });
  }

  /* --- Boot ----------------------------------------------------------------- */

  function boot() {
    initAnnouncement();
    initHeaderShrink();
    initVariants();
    initUpsellSliders(document);
    syncCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    releaseAllLocks();
    boot();
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-bl-cart]')) openCart();
  });
})();
